/**
 * Portal 全局状态 — 对应原版 Portal IIFE (app.js)
 * 管理：认证、主题、站点配置、会话状态
 */
import { reactive, computed } from 'vue'

export interface SiteConfig {
  site_name: string
  site_logo: string
  site_description: string
  site_keywords: string
  site_subtitle: string
  assistant_name: string
  icp_number: string
  footer_copyright: string
  disclaimer_text: string
  help_url: string
  download_url: string
  terms_url: string
  privacy_url: string
  chat_header_title: string
  chat_header_subtitle: string
  chat_suggestions_mode: string
  chat_suggestions: string
}

interface MentionRole {
  role_key: string
  role_name: string
}

interface UploadFile {
  url: string
  type: string
  name: string
  size?: number
  extracted_text?: string | null
}

const state = reactive({
  // 认证
  user: null as any,
  _guest: true,

  // 主题
  theme: (localStorage.getItem('portal_theme') || 'light') as 'light' | 'dark',

  // 会话
  currentRequestId: null as string | null,
  currentConversationId: null as string | null,
  isStreaming: false,

  // 网关模式
  gatewayMode: 'fast' as 'fast' | 'expert',
  globalMode: 'chat' as 'chat' | 'create' | 'debate' | 'zen',

  // 角色
  selectedRoles: [] as string[],
  availableRoles: [] as any[],

  // 调度
  autoDispatch: true,
  dispatchEnabled: null as boolean | null,

  // 站点配置
  siteConfig: {} as Partial<SiteConfig>,

  // 用户个人资料（与 profile.php 同步，供全局组件使用）
  userDisplayName: '',
  userAvatar: '',

  // AbortController（用于取消 SSE）
  abortController: null as AbortController | null,
})

// --- 认证 ---
function getToken() {
  return state.user?.token || localStorage.getItem('portal_token') || ''
}

const isLoggedIn = computed(() => !!(state.user?.token))
const isAdmin = computed(() => state.user?.role === 'admin')
const isDemo = computed(() => !!state.user?.demo)

/**
 * 访客演示身份：未登录时静默换取一个只读的演示账号，
 * 让访客无需输入账号密码就能看到精选演示数据。
 */
async function demoLogin() {
  const res = await fetch('/api/auth.php?action=demo_login', { method: 'POST' })
  const contentType = res.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) return false
  const data = await res.json()
  if (!res.ok || !data.token) return false
  state.user = data
  state._guest = false
  localStorage.setItem('portal_token', data.token)
  localStorage.setItem(
    'portal_user',
    JSON.stringify({
      username: data.username,
      role: data.role,
      client_name: data.client_name,
      client_key_id: data.client_key_id,
      demo: true,
    }),
  )
  return true
}

async function login(username: string, password: string) {
  const res = await fetch(
    (import.meta.env.DEV ? '' : '') + '/api/auth.php?action=login',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    },
  )
  const contentType = res.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error(`登录服务返回异常（HTTP ${res.status}）`)
  }
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || '登录失败')
  state.user = data
  state._guest = false
  localStorage.setItem('portal_token', data.token)
  localStorage.setItem(
    'portal_user',
    JSON.stringify({
      username: data.username,
      role: data.role,
      client_name: data.client_name,
      client_key_id: data.client_key_id,
    }),
  )
  return data
}

async function logout() {
  try {
    await fetch('/api/auth.php?action=logout', {
      headers: { Authorization: 'Bearer ' + getToken() },
    })
  } catch (_) {}
  state.user = null
  localStorage.removeItem('portal_token')
  localStorage.removeItem('portal_user')
}

async function checkAuth() {
  const token = getToken()
  if (!token) {
    state._guest = true
    return false
  }
  try {
    const res = await fetch('/api/auth.php?action=check', {
      headers: { Authorization: 'Bearer ' + token },
    })
    const data = await res.json()
    if (data.valid) {
      state._guest = false
      const cached = JSON.parse(localStorage.getItem('portal_user') || '{}')
      state.user = {
        ...cached,
        token,
        role: data.role,
        username: data.username,
      }
      return true
    }
  } catch (_) {}
  state._guest = true
  state.user = null
  localStorage.removeItem('portal_token')
  localStorage.removeItem('portal_user')
  return false
}

// --- 主题 ---
function setTheme(theme: 'light' | 'dark') {
  state.theme = theme
  document.documentElement.setAttribute('data-theme', theme)
  localStorage.setItem('portal_theme', theme)
}

function toggleTheme() {
  setTheme(state.theme === 'dark' ? 'light' : 'dark')
}

// --- API ---
const API_BASE = '/api/'

async function apiGet(path: string, params: Record<string, any> = {}, silent = false) {
  const url = new URL(API_BASE + path, window.location.origin)
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v)
  })
  url.searchParams.set('_t', String(Date.now()))
  const res = await fetch(url, {
    headers: { Authorization: 'Bearer ' + getToken() },
    cache: 'no-store',
  })
  if (res.status === 401) {
    if (!silent && !state._guest) {
      forceLogout()
      throw new Error('登录已过期，请重新登录')
    }
    throw new Error('请登录后使用')
  }
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || '请求失败')
  return data
}

async function apiPost(path: string, body: Record<string, any> = {}, silent = false) {
  const res = await fetch(API_BASE + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + getToken(),
    },
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    if (!silent && !state._guest) {
      forceLogout()
      throw new Error('登录已过期，请重新登录')
    }
    throw new Error('请登录后使用')
  }
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || '请求失败')
  return data
}

function forceLogout() {
  state.user = null
  localStorage.removeItem('portal_token')
  localStorage.removeItem('portal_user')
  setTimeout(() => location.reload(), 1500)
}

// --- 用户个人资料 ---
async function loadUserProfile() {
  try {
    const data = await apiGet('profile.php?action=get', {}, true)
    const d = data.data || {}
    state.userDisplayName = d.display_name || d.username || ''
    state.userAvatar = d.avatar || ''
  } catch (_) {
    // 静默失败
  }
}

function updateUserProfile(displayName: string, avatar: string) {
  state.userDisplayName = displayName
  state.userAvatar = avatar
}

// --- SSE 流式 ---
function createSSEConnection(
  body: any,
  callbacks: {
    onStart?: (e: any) => void
    onMessage?: (e: any) => void
    onRoleDone?: (e: any) => void
    onMediaDone?: (e: any) => void
    onDone?: (e: any) => void
    onPhase?: (e: any) => void
    onPhaseDone?: (e: any) => void
    onError?: (msg: string) => void
    onStop?: () => void
    onTokenWarning?: (msg: string) => void
    onDispatcherResult?: (e: any) => void
  },
) {
  const abortController = new AbortController()
  state.abortController = abortController
  state.isStreaming = true

  fetch(API_BASE + 'chat_stream.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + getToken(),
    },
    body: JSON.stringify(body),
    signal: abortController.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        const text = await response.text()
        let msg = '请求失败'
        try {
          const err = JSON.parse(text)
          msg = err.error || msg
        } catch (_) {
          if (text) msg = text.substring(0, 200)
        }
        callbacks.onError?.(msg)
        return
      }

      const reader = response.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let receivedDone = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const event = JSON.parse(line.slice(6))
            if (event.type === 'done' || event.type === 'error') {
              receivedDone = true
            }
            processSSEEvent(event, callbacks)
          } catch (_) {}
        }
      }

      if (!receivedDone) {
        callbacks.onError?.('服务端处理异常，请稍后重试')
      }
    })
    .catch((err) => {
      if (err.name === 'AbortError') {
        callbacks.onStop?.()
      } else {
        callbacks.onError?.(err.message || '网络连接失败')
      }
    })
    .finally(() => {
      state.isStreaming = false
      state.abortController = null
    })

  return abortController
}

function processSSEEvent(event: any, callbacks: any) {
  switch (event.type) {
    case 'start': callbacks.onStart?.(event); break
    case 'message': callbacks.onMessage?.(event); break
    case 'role_done': callbacks.onRoleDone?.(event); break
    case 'media_done': callbacks.onMediaDone?.(event); break
    case 'phase': callbacks.onPhase?.(event); break
    case 'phase_done': callbacks.onPhaseDone?.(event); break
    case 'done': callbacks.onDone?.(event); break
    case 'error': callbacks.onError?.(event.message || event.error || '未知错误'); break
    case 'token_warning': callbacks.onTokenWarning?.(event.message); break
    case 'dispatcher_result': callbacks.onDispatcherResult?.(event); break
    default:
      if (event.role_key && event.response) callbacks.onRoleDone?.(event)
      else if (event.summary) callbacks.onDone?.(event)
  }
}

function stopStreaming() {
  if (state.abortController) {
    state.abortController.abort()
    state.abortController = null
  }
  state.isStreaming = false
}

// --- 站点配置 ---
async function loadSiteConfig() {
  try {
    const data = await apiGet('site_config.php?all=1')
    state.siteConfig = data.data || {}
  } catch (_) {
    state.siteConfig = {}
  }
}

// --- 会话 ---
function generateUUID() {
  return 'conv_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 10)
}
function getOrCreateConversationId() {
  if (!state.currentConversationId) {
    state.currentConversationId = generateUUID()
  }
  return state.currentConversationId
}
function newConversationId() {
  state.currentConversationId = generateUUID()
}

export function usePortal() {
  return {
    // 响应式状态
    state,
    isLoggedIn,
    isAdmin,
    isDemo,
    // 认证
    login, demoLogin, logout, checkAuth, getToken,
    // 用户个人资料
    loadUserProfile, updateUserProfile,
    // 主题
    setTheme, toggleTheme,
    // API
    apiGet, apiPost,
    // SSE
    createSSEConnection, stopStreaming,
    // 站点配置
    loadSiteConfig,
    // 会话
    getOrCreateConversationId, newConversationId,
  }
}
